<template>
  <div class="opc-capacity-config-row opc-capacity-config-row-v2">
    <div class="opc-capacity-config-row__license">
      <div class="opc-capacity-config-row__license__content">
        <div class="opc-capacity-config-row__license__content--cycle">
          <!-- Capacity Section -->
          <div class="opc-capacity-config-row__capacity">
            <!-- Text View Model -->
            <config-text-row
              v-if="textViewModel"
              :title="capitalizeFirstLetter($t(capacityUnitI18nKey))"
              :value="selectCapacity"
            />

            <!-- Capacity Dropdown -->
            <div v-else-if="showSelectCapacityDropdown" class="opc-capacity-config-row__capacity--wrap">
              <span>{{capitalizeFirstLetter($t(capacityUnitI18nKey))}}</span>
              <zm-select
                data-testid="checkout-select-11"
                ref="attendeeSelect"
                :size="selectCapacitySize"
                v-model="selectedValue"
                :aria-label="selectLabel || `${planName} ${$t(capacityUnitI18nKey)}`"
                @update:model-value="onSwitchCapacity"
                floating-content-class="opc-capacity-config-row__capacity--popper"
                class="opc-capacity-config-row__capacity--select"
              >
                <zm-select-option-group
                  v-for="group in capacityGroupOptions.filter(group => group?.options?.length)"
                  :key="group.label"
                  :label="group.label"
                >
                  <zm-select-option
                    v-for="p in setCapacityOptions(group.options)"
                    :key="combineCapacity(planType, p.capacity, group.cycle)"
                    :value="combineCapacity(planType, p.capacity, group.cycle)"
                    :label="capacityFormatter(p.capacity)"
                    :disabled="p.disabled"
                    :tooltip-content="p.tooltips"
                  />
                </zm-select-option-group>
              </zm-select>
            </div>

            <!-- Capacity View -->
            <div v-else class="opc-capacity-config-row__cycle__title">
              {{ selectCapacity }} <span class="opc-capacity-config-row__capacity--unit">{{ $t(capacityUnitI18nKey) }}</span>
            </div>

            <!-- Capacity Tips -->
            <slot v-if="$slots.capacityTips" name="capacityTips" />
            <zm-tooltip
              v-else-if="capacityTips"
              class="opc-capacity-config-row__tooltips"
              role="button"
              disallow-learn-about
              aria-label="Learn more about Select the number of licenses"
              :content="capacityTips"
              placement="bottom"
              @input="toggleCapacityTips"
            >
              <tips-icon type="info" aria-label="Learn more about Select the number of licenses" />
            </zm-tooltip>
          </div>

          <!-- Cycle Row (Use Free Trial Config Cycle) -->
          <config-cycle
            ref="configCycle"
            :billing-cycle="billingCycle"
            :billing-cycle-list="billingCycleList"
            :billing-cycle-group-options="billingCycleGroupOptions.filter(group => group?.options?.length)"
            :show-monthly-price-from-cycle="showMonthlyPriceFromCycle"
            :plan-name="planName"
            :text-view-model="isEventSaasOrEventSassCore(planType, false) && billingCycleGroupOptions.filter(group => group?.options?.length).length <= 1"
            @switch-cycle-action="switchBillingCycle"
            @sync-cycle="changeBillingCycle"
          />
        </div>

        <!-- License Section (hide when license <= 1 for free trial) -->
        <div v-if="license > 1" class="opc-capacity-config-row__input__number">
          <input-number-with-popover
            :modelValue="license"
            @update:modelValue="changeLicense"
            size="lg"
            controls-position="both"
            :min="minHosts"
            :max="maxHosts"
            :increase-tips="getIncreaseTips(selectCapacity, license)"
            :decrease-tips="decreaseTips"
            :unit="inputUnit"
            :label="`${trackingPlanKey} + Select the number of ${planUnit}`"
          />

          <!-- License Tips -->
          <slot v-if="$slots.licenseTips" name="licenseTips" />
          <zm-tooltip
            v-else-if="licenseTips"
            class="opc-capacity-config-row__tooltips"
            role="button"
            disallow-learn-about
            aria-label="Learn more about Select the number of licenses"
            :content="licenseTips"
            placement="bottom"
            @input="toggleLicenseTips"
          >
            <tips-icon type="info" aria-label="Learn more about Select the number of licenses" />
          </zm-tooltip>
        </div>
      </div>

      <!-- Remove Button -->
      <zm-button
        data-testid="checkout-button-12"
        type="link"
        ghost
        size="mini"
        @click="onDelete"
        :aria-label="`remove ${trackingPlanKey}`"
        :class="{
          'opc-capacity-config-row__delete-btn': true,
          'opc-capacity-config-row__delete--btn': !deleteVisible,
        }"
      >
        <i class="opc-capacity-config-row__delete--icon zm-icon-close" />
      </zm-button>
    </div>

    <!-- Amount Section (free trial unit suffix) -->
    <p class="opc-cycle__plan__price">
      <span class="opc-cycle__plan__price--amount">
        {{ amountInfo.amount }}
      </span>
      <span class="opc-cycle__plan__price--unit" v-html="amountInfo.cycleLabel"></span><price-v-a-t-tip />
    </p>

    <!-- License Alert -->
    <slot v-if="$slots.licenseAlert" name="licenseAlert" />
    <zm-banner show-close
      v-else-if="licenseAlert"
      class="opc-common-info-alert"
      type="warning"
      show-icon
    >
      <p v-html="licenseAlert" @click="trackingAlert" />
    </zm-banner>

    <!-- Cycle Disabled Alert -->
    <slot v-if="$slots.cycleAlert && showCycleAlert && firstDisabledCycleAlertItem" name="cycleAlert" />
    <zm-banner show-close
      v-else-if="showCycleAlert && firstDisabledCycleAlertItem?.alert"
      class="opc-common-info-alert"
      type="warning"
      show-icon
    >
      <p v-html="firstDisabledCycleAlertItem.alert" @click="trackingAlert" />
    </zm-banner>

    <!-- Divider -->
    <hr v-if="dividerVisible" />
  </div>

</template>

<script>
import CapacityConfigRowV2 from '@/components/capacity/v2/config-row';
import ConfigCycleV2FreeTrial from '@/components/service/config-billing-cycle/v2/free-trial';
import discountedMonthPrice from '@/common/mixin/discounted-month-price';

export default {
  name: 'ConfigRowV2FreeTrial',
  extends: CapacityConfigRowV2,
  components: { ConfigCycle: ConfigCycleV2FreeTrial },
  mixins: [discountedMonthPrice],
  computed: {
    amountInfo() {
      const { amount = 0, billing_cycle, esmd_amount = 0 } = this?.cartSelectedPlan ?? {};

      // Check if we should use downsell amount - use passed props
      let effectiveAmount = amount;
      if (this.cartSelectedPlan?.isDownsellAmountOverwrite &&
        this.cartSelectedPlan?.downsellAmount &&
        this.currentLicense !== undefined) {
        const newLicense = this.cartSelectedPlan?.cartInfo?.selected_number || 0;

        // If license is decreasing, use downsell amount
        if (newLicense < this.currentLicense && newLicense > 0) {
          effectiveAmount = this.cartSelectedPlan.downsellAmount;
        }
      }

      effectiveAmount = effectiveAmount + esmd_amount;

      return {
        amount: this.getFormattedPriceOfMonthWithDownsell({amount: effectiveAmount, billing_cycle: this.billingCycle}, { forceDiff: true }),
        cycleLabel: this.$t(billing_cycle ? this.$t('billing.trial.unit_suffix', [this.getMergedBilledText(this.billingCycle, effectiveAmount, this.unit)]) : '')
      };
    },
  }
};
</script>

<style lang='scss' scoped>
@import '@zoom/billing-responsive/styles/responsive.scss';
@import '@/styles/scss-utils.scss';
@import './config-row.scss';
</style>
